import React, {useState} from 'react'
import {Button, Form} from 'semantic-ui-react'
import API from '../../../api'
import Dialog from '../../dialog'
import {getApiResponseErrorMessage} from '../../error'
import {ApplicationModel} from '../../../models'
import {typeOptions, statusOptions, fieldNames} from '../options'
import * as Yup from 'yup'
import useSWR  from 'swr'
import {GlossaryFetcher} from "../../../fetch/glossary";

const AppDialog = ({ title, open, onSubmit, onClose, item }) => {

    const initialState = {
        loading: false,
        id: null,
        name: '',
        type: '',
        vendor: '',
        firmReference: '',
        docLink: '',
        businessArea: '',
        lastAttestedAt: '',
        lastAttestedBy: '',
        status: ''
    }

    const [state, setState] = useState(initialState)
    const [errors, setError] = useState({})

    const { data : appDetails, errorAppDetails } = useSWR(item && item.id ? `/application/details/${item.id}` : null, GlossaryFetcher, { errorRetryCount: 2 })

    React.useEffect(() => {
        if(appDetails && appDetails.data) {
            setState({
                ...state,
                id: appDetails.data.id,
                name: appDetails.data.name,
                type: appDetails.data.type,
                vendor: appDetails.data.vendor ? appDetails.data.vendor : '',
                firmReference: appDetails.data.firmReference ? appDetails.data.firmReference : '',
                businessArea: appDetails.data.businessArea ? appDetails.data.businessArea : '',
                docLink: appDetails.data.docLink ? appDetails.data.docLink : '',
                lastAttestedAt: appDetails.data.lastAttestedAt ? appDetails.data.lastAttestedAt : '',
                lastAttestedBy: appDetails.data.lastAttestedBy ? appDetails.data.lastAttestedBy : '',
                status: appDetails.data.status
            })
        }

    }, [appDetails])

    const { data : userList, errorUserList } = useSWR(`/user/list`, GlossaryFetcher, { errorRetryCount: 2 })

    const [users, setUsers] = useState([])
    React.useEffect(() => {
        if(userList) {
            let _tmpUsers = [];
            userList.data.map(user =>{
                _tmpUsers.push({
                    value: user.id,
                    text: user.firstName + " " + user.lastName
                })
            })
            setUsers(_tmpUsers)
        }
    }, [userList])

    const formSchema = Yup.object().shape({
        name: Yup.string()
            .min(3, 'Must be at least 3 characters')
            .max(255)
            .required(),
        businessArea: Yup.string()
            .max(255),
        vendor: Yup.string()
            .max(255),
        type: Yup.string()
            .required(),
        status: Yup.string()
            .required()
    });

    const handleInputChange = (e, {name, value}) => {

        if (errors[name]) {
            setError( errors => {
                return {
                    ...errors,
                    [name]: false
                }
            })
        }

        setState( state => {
            return {
                ...state,
                [name]: value
            }
        })

    }

    const handleCallback = (callback, data) => {

        setState(initialState)
        setError({})


        if (Array.isArray(callback)) {
            callback.forEach( func => {
                if (typeof func === 'function') {
                    func(data)
                }
            })
        } else {
            if (typeof callback === 'function') {
                callback(data)
            }
        }

    }

    const handleOnSubmit = () => {


        setError({})

        formSchema.validate(state, {abortEarly: false}).then(() => {

            setState( state => {
                return {
                    ...state,
                    loading: true
                }
            })

            API.post('/application/save', new ApplicationModel(state))
                .then( response => handleCallback([onSubmit, onClose], response.data))
                .catch( err => {

                    setState( state => {
                        return {
                            ...state,
                            loading: false
                        }
                    })

                    //if(err.response) console.log(err.response)
                    if (err.response && err.response.data.messages) {

                        const errorFields = {}

                        for (const [key, value] of Object.entries(err.response.data.messages)) {
                            errorFields[key] = getApiResponseErrorMessage({key: value, prefix: fieldNames[key]})
                        }

                        setError( err => {
                            return {
                                ...err,
                                ...errorFields
                            }
                        })

                    }

                })

        }).catch( errors => {

            console.log(errors)

            const errorFields = {}

            errors.inner.forEach( error => errorFields[error.path] = error.message.replace(error.path, fieldNames[error.path]) )

            setError( err => {
                return {
                    ...err,
                    ...errorFields
                }
            })

        })

    }

    return (
        <Dialog open={open} onClose={() => handleCallback(onClose)}  closeOnEscape={false} closeOnDimmerClick={false}>

            <Form onSubmit={handleOnSubmit} loading={state.loading}>

                <Dialog.Header>
                    {item && item.id ? "Edit application" : title}
                </Dialog.Header>

                <Dialog.Content>

                    <Form.Group widths="equal">

                        <Form.Field>

                            <Form.Input
                                name="name"
                                error={errors.name}
                                label={fieldNames.name}
                                value={state.name}
                                onChange={handleInputChange}
                            />

                            <br />

                            <Form.Select
                                name="type"
                                label={fieldNames.type}
                                options={typeOptions}
                                error={errors.type}
                                placeholder={"Select Type"}
                                value={state.type}
                                onChange={handleInputChange}
                                icon={"chevron down"}
                            />

                            <br />

                            <Form.Select
                                name="status"
                                label={fieldNames.status}
                                options={statusOptions}
                                error={errors.status}
                                placeholder={"Select Status"}
                                value={state.status}
                                onChange={handleInputChange}
                                icon={"chevron down"}
                            />

                            <br />

                            <Form.Input
                                name="vendor"
                                label={fieldNames.vendor}
                                onChange={handleInputChange}
                                value={state.vendor}
                            />

                            <br />

                            <Form.Input
                                name="firmReference"
                                label={fieldNames.firmReference}
                                onChange={handleInputChange}
                                value={state.firmReference}
                            />


                        </Form.Field>

                        <Form.Field>

                            <Form.Input
                                name="docLink"
                                label={fieldNames.docLink}
                                onChange={handleInputChange}
                                value={state.docLink}
                            />

                            <br />

                            <Form.Input
                                name="businessArea"
                                label={fieldNames.businessArea}
                                onChange={handleInputChange}
                                value={state.businessArea}
                            />

                            <br />

                            <Form.Input
                                type="date"
                                name="lastAttestedAt"
                                label={fieldNames.lastAttestedAt}
                                onChange={handleInputChange}
                                value={state.lastAttestedAt}
                            />

                            <br />

                            <Form.Select
                                name="lastAttestedBy"
                                label={fieldNames.lastAttestedBy}
                                options={users}
                                clearable={true}
                                placeholder={"Select User"}
                                onChange={handleInputChange}
                                value={state.lastAttestedBy}
                            />

                            <br />


                        </Form.Field>

                    </Form.Group>

                </Dialog.Content>

                <Dialog.Actions>
                    <Button type="submit" primary>
                        Submit
                    </Button>
                    <Button onClick={() => handleCallback(onClose)}>
                        Cancel
                    </Button>
                </Dialog.Actions>

            </Form>
        </Dialog>
    )

}


export default AppDialog